import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Users, Building2, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RegionalAssignmentCards — iter-78 part 6 (Master Architecture).
 *
 * Renders one assignment card per direct-report tier in the current user's
 * region, matching the "Regional Assignment Card" concept the RM hands off
 * to each supervisor (and each supervisor hands off to each crew leader).
 *
 *   role=manager    → one card per Supervisor in scope. Each card shows the
 *                     cities & communities carved for that supervisor plus
 *                     the crew leaders & agents in their downstream chain.
 *   role=supervisor → one card per Crew Leader they manage. Each card shows
 *                     the cities/communities & the agents in that crew.
 *
 * A Scoop chip strip lets the caller cherry-pick cities to filter the
 * property list on every card at once (mirror of the CrewDelegation scoop UI).
 * If no scoop cities are selected, every card shows its full scope.
 *
 * Only renders when the caller's role fits — otherwise returns null.
 */
export function RegionalAssignmentCards({ user }) {
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [region, setRegion] = useState(null);
  const [scoopCities, setScoopCities] = useState([]);
  const [tiers, setTiers] = useState({}); // Iter-78 pt 12 · per-user tier badge
  const [loading, setLoading] = useState(true);

  const targetRole =
    user?.role === 'supervisor' ? 'crew_leader'
    : ['manager', 'company_manager', 'admin'].includes(user?.role) ? 'supervisor'
    : null;

  useEffect(() => {
    if (!targetRole) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [teamRes, assignRes, propRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/company/staff-assignments`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/properties`, { credentials: 'include' }),
        ]);
        const teamData = teamRes.ok ? await teamRes.json() : [];
        const assignData = assignRes.ok ? await assignRes.json() : [];
        const propData = propRes.ok ? await propRes.json() : [];
        if (cancelled) return;
        // Scope to the caller's own region — RM/supervisor only see their
        // region; admin/CEO defaults to the first non-unassigned region for
        // demo purposes (they can still swap regions via the region switcher).
        const entries = (teamData || []).filter(
          (e) => e.region?.region_id && e.region.region_id !== '_unassigned',
        );
        const scope = user?.region_id
          ? entries.find((e) => e.region.region_id === user.region_id) || entries[0]
          : entries[0];
        setRegion(scope?.region || null);
        setStaff(scope?.staff || []);
        setAssignments(Array.isArray(assignData) ? assignData : []);
        setProperties(Array.isArray(propData) ? propData : []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.region_id, targetRole]);

  const assignByUserId = useMemo(() => {
    const m = {};
    for (const a of assignments) if (a?.user_id) m[a.user_id] = a;
    return m;
  }, [assignments]);

  const propById = useMemo(() => {
    const m = {};
    for (const p of properties) if (p?.property_id) m[p.property_id] = p;
    return m;
  }, [properties]);

  // Shared regional teams have their own joint-ownership card above this legacy view.
  const targetStaff = staff.filter((s) => s.role === targetRole && !assignByUserId[s.user_id]?.regional_order_id);

  // Iter-78 pt 12 — fetch each parent tier so the assignment cards can
  // display Standard/Silver/Gold/Platinum status badges.
  useEffect(() => {
    if (!targetStaff.length) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        targetStaff.slice(0, 60).map((s) =>
          fetch(`${BACKEND_URL}/api/agent-status/${s.user_id}`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ),
      );
      if (cancelled) return;
      const m = {};
      results.forEach((res) => { if (res?.user_id) m[res.user_id] = res.tier; });
      setTiers(m);
    })();
    return () => { cancelled = true; };
  }, [targetStaff.map((s) => s.user_id).join(',')]);

  // Cities in the region — pulled from properties tagged to any staff in
  // the caller's scope (drives the Scoop chip strip).
  const cities = useMemo(() => {
    const bag = new Set();
    for (const p of properties) {
      if (p?.city && (!region?.region_id || p.region_id === region.region_id)) {
        bag.add(p.city.trim());
      }
    }
    return Array.from(bag).sort();
  }, [properties, region?.region_id]);

  const toggleScoop = (c) =>
    setScoopCities((prev) => (prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]));

  if (!targetRole || (!loading && !targetStaff.length)) return null;

  const parentCard = (parent) => {
    const parentAssign = assignByUserId[parent.user_id] || {};
    // Properties + cities carved for this parent.
    const propIds = parentAssign.assigned_properties || [];
    const parentCities = parentAssign.assigned_cities || [];
    let scopedProps = propIds.map((pid) => propById[pid]).filter(Boolean);
    if (scoopCities.length) {
      scopedProps = scopedProps.filter((p) => scoopCities.includes((p?.city || '').trim()));
    }
    // Downstream chain — who reports to this parent.
    const downstream = staff.filter(
      (s) => (assignByUserId[s.user_id]?.managed_by) === parent.user_id,
    );
    const secondTier =
      user?.role === 'supervisor'
        ? [] // supervisor's target is crew_leader — no further tier to render inline
        : staff.filter((s) => {
            const a = assignByUserId[s.user_id];
            if (!a) return false;
            const mgrOfMgr = assignByUserId[a.managed_by]?.managed_by;
            return mgrOfMgr === parent.user_id && s.role === 'agent';
          });

    return (
      <Card
        key={parent.user_id}
        className="bg-zinc-800 border-2 border-[#C9A962]/40 p-4 hover:border-[#C9A962] transition-colors min-w-0 [overflow-wrap:anywhere]"
        data-testid={`assignment-card-${parent.user_id}`}
      >
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <div className="min-w-0">
            <p className="font-bold text-white truncate text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {parent.name || 'Unnamed'}
            </p>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {parent.badge_number || '—'} · {targetRole.replace('_', ' ')}
            </p>
          </div>
          <Badge className="bg-zinc-700 text-white text-[10px] flex-shrink-0">
            {scopedProps.length} props · {downstream.length + secondTier.length} team
          </Badge>
        </div>

        {/* Iter-78 pt 12 — Tier badge (Standard / Silver / Gold / Platinum). */}
        {tiers[parent.user_id] && (
          <div className="mb-2">
            <span
              className="inline-block text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{
                backgroundColor:
                  tiers[parent.user_id] === 'Platinum' ? '#e5e4e2' :
                  tiers[parent.user_id] === 'Gold'     ? '#FFD60A' :
                  tiers[parent.user_id] === 'Silver'   ? '#C0C0C0' :
                                                        '#71717a',
                color: tiers[parent.user_id] === 'Standard' ? '#fff' : '#000',
              }}
              data-testid={`assignment-tier-${parent.user_id}`}
            >
              ★ {tiers[parent.user_id]}
            </span>
          </div>
        )}

        {parentCities.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Cities</p>
            <div className="flex flex-wrap gap-1">
              {parentCities.slice(0, 6).map((c) => (
                <Badge key={c} className="bg-[#C9A962]/15 border border-[#C9A962]/40 text-[#C9A962] text-[10px]">
                  {c}
                </Badge>
              ))}
              {parentCities.length > 6 && (
                <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">+{parentCities.length - 6}</Badge>
              )}
            </div>
          </div>
        )}

        {scopedProps.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Communities</p>
            <ul className="space-y-0.5 max-h-24 overflow-y-auto pr-1">
              {scopedProps.slice(0, 8).map((p) => (
                <li key={p.property_id} className="text-xs text-zinc-300 break-words" title={p.address || p.property_name}>
                  · {p.property_name}
                  {p.city ? <span className="text-zinc-500"> — {p.city}</span> : null}
                </li>
              ))}
              {scopedProps.length > 8 && (
                <li className="text-[10px] text-zinc-500">+ {scopedProps.length - 8} more</li>
              )}
            </ul>
          </div>
        )}

        {downstream.length > 0 && (
          <div className="mb-1">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              {user?.role === 'supervisor' ? 'Site agents' : 'Crew leaders'}
            </p>
            <div className="flex flex-wrap gap-1">
              {downstream.slice(0, 6).map((s) => (
                <Badge
                  key={s.user_id}
                  className="text-[10px] bg-purple-500/15 text-purple-200 border border-purple-500/40"
                  data-testid={`assignment-child-${s.user_id}`}
                >
                  {s.name?.split(' ')[0] || s.badge_number}
                </Badge>
              ))}
              {downstream.length > 6 && (
                <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">+{downstream.length - 6}</Badge>
              )}
            </div>
          </div>
        )}

        {/* Second tier — only rendered on the RM view (supervisor→CL→agents) */}
        {secondTier.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 mt-2">Site agents</p>
            <div className="flex flex-wrap gap-1">
              {secondTier.slice(0, 8).map((s) => (
                <Badge
                  key={s.user_id}
                  className="text-[10px] bg-orange-500/15 text-orange-200 border border-orange-500/40"
                >
                  {s.name?.split(' ')[0] || s.badge_number}
                </Badge>
              ))}
              {secondTier.length > 8 && (
                <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">+{secondTier.length - 8}</Badge>
              )}
            </div>
          </div>
        )}

        {downstream.length === 0 && scopedProps.length === 0 && (
          <p className="text-[11px] text-zinc-500 italic">
            No assignments yet — carve cities &amp; communities above then save to this {targetRole.replace('_', ' ')}.
          </p>
        )}
      </Card>
    );
  };

  const headerLabel = user?.role === 'supervisor'
    ? 'SUPERVISOR ASSIGNMENT CARDS'
    : 'REGIONAL ASSIGNMENT CARDS';

  return (
    <Card
      className="bg-zinc-900 border-2 p-6 mb-2"
      style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
      data-testid="regional-assignment-cards"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <MapPin className="h-5 w-5 mr-2 text-[#C9A962]" />
          {headerLabel} · <span className="ml-2 text-[#C9A962]">{region?.region_code || '—'}</span>
          <span className="ml-1 text-zinc-300">{region?.name || ''}</span>
          <Badge className="ml-2 bg-[#C9A962] text-black text-[10px] font-bold" data-testid="assignment-cards-count">
            {targetStaff.length} cards
          </Badge>
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          One card per {targetRole.replace('_', ' ')} · up to 6 per region
        </span>
      </div>

      {/* Scoop chip strip — same UX as the Crew Leader delegation view. */}
      {cities.length > 0 && (
        <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 px-3 py-2 mb-4" data-testid="assignment-scoop-strip">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1 mr-1">
              <Filter className="h-3 w-3" /> Scoop cities · <span className="text-white">{scoopCities.length || 'all'}</span>
            </p>
            {cities.map((c) => {
              const on = scoopCities.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleScoop(c)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-bold transition ${
                    on
                      ? 'bg-[#C9A962] text-black border border-[#C9A962]'
                      : 'bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-[#C9A962]'
                  }`}
                  data-testid={`assignment-scoop-${c.replace(/\s+/g, '-')}`}
                >
                  {c}
                </button>
              );
            })}
            {scoopCities.length > 0 && (
              <button
                type="button"
                onClick={() => setScoopCities([])}
                className="ml-1 text-[10px] text-zinc-500 hover:text-white underline"
                data-testid="assignment-scoop-clear"
              >
                clear
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : targetStaff.length === 0 ? (
        <p className="text-sm text-zinc-500 italic">
          No {targetRole.replace('_', ' ')}s in {region?.region_code || 'your region'} yet.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="assignment-cards-grid">
          {targetStaff.map((s) => parentCard(s))}
        </div>
      )}
    </Card>
  );
}

export default RegionalAssignmentCards;
